import React from 'react';
import { MapPin, Plane, Sparkles, GraduationCap, Building2, Star } from 'lucide-react';
const textBlocks = [{
  title: 'El Origen',
  icon: GraduationCap,
  flag: '2020 — São Paulo, Brasil',
  body: 'Mi historia en el mundo de los alisados comenzó durante la pandemia, cuando tuve la oportunidad de formarme en São Paulo, Brasil. Allí adquirí conocimientos profundos sobre alisados orgánicos, salud capilar y el cuidado profesional del cabello. Más adelante regresé a Colombia, donde continué perfeccionando esta hermosa profesión.'
}, {
  title: 'La Llegada a EE.UU',
  icon: Building2,
  flag: '2023 — Nueva York, desde cero',
  body: 'En 2023 llegué a Estados Unidos y comencé desde cero, ofreciendo mis servicios a domicilio. Con el tiempo adapté un pequeño espacio en la sala de mi casa para atender a mis clientas con amor, profesionalismo y excelencia. Cada cabello transformado y cada clienta satisfecha fueron impulsándome a crecer cada día más.'
}, {
  title: 'Hoy',
  icon: Sparkles,
  flag: 'Hoy',
  body: 'Hoy, con orgullo, puedo decir que soy educadora especializada en alisados orgánicos — formando a otras personas que, al igual que yo, desean cambiar su vida a través de este hermoso oficio.'
}, {
  title: 'Mi Visión',
  icon: Star,
  flag: 'Mi Visión',
  body: 'Seguir construyendo una marca reconocida por la calidad, la honestidad y la innovación en el cuidado capilar. Quiero inspirar a más mujeres y emprendedores a creer en sus sueños — demostrando que no importa de dónde vengas ni con cuánto empieces.'
}];
const stats = [{
  icon: <Plane className="w-6 h-6 metallic-gold-icon" data-id="element-355" />,
  value: '+3.5',
  label: 'Años en EE.UU'
}, {
  icon: <Sparkles className="w-6 h-6 metallic-gold-icon" data-id="element-356" />,
  value: '2020',
  label: 'Brasil · Formación Profesional'
}, {
  icon: <MapPin className="w-6 h-6 metallic-gold-icon" data-id="element-357" />,
  value: 'Queens',
  label: 'New York · Base hoy'
}];
const timeline = [{
  year: '2020',
  place: 'São Paulo, Brasil',
  desc: 'Formación profesional en alisados orgánicos.'
}, {
  year: '2021 - 2022',
  place: 'Colombia',
  desc: 'Perfeccionamiento y construcción de clientela.'
}, {
  year: '2023 - Hoy',
  place: 'Long Island City, Queens NY',
  desc: 'Educadora certificada & fundadora de su propio estudio.'
}];
export const MiHistoria = () => {
  return <section className="py-20 bg-[#F5ECD7]" id="mi-historia" data-id="element-358">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-359">
        {/* Heading */}
        <div className="text-center mb-16" data-id="element-360">
          <p className="text-sm font-bold tracking-widest text-gold-600 uppercase mb-3" data-id="element-361">
            Mi Historia
          </p>
          <h2 className="text-4xl md:text-5xl font-serif text-neutral-900 leading-tight mb-4" data-id="element-362">
            Visión de{' '}
            <span className="metallic-gold-text italic" data-id="element-363">Stefania Becerra</span>
          </h2>
          <p className="font-serif italic text-lg md:text-xl text-neutral-700 max-w-2xl mx-auto" data-id="element-364">
            De Colombia a Queens, Nueva York — construyendo sueños con cada
            cabello transformado.
          </p>
          <div className="w-24 h-1 bg-gold-300 mx-auto rounded-full mt-6" data-id="element-365"></div>
        </div>

        {/* Intro: photo + first text blocks */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16" data-id="element-366">
          {/* Image — premium framed portrait */}
          <div className="relative w-full max-w-md mx-auto lg:mx-0" data-id="element-367">
            {/* Ambient glows */}
            <div className="absolute -bottom-8 -left-8 w-52 h-52 bg-gold-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40 z-0" data-id="element-368"></div>
            <div className="absolute -top-8 -right-8 w-52 h-52 bg-rosegold rounded-full mix-blend-multiply filter blur-3xl opacity-30 z-0" data-id="element-369"></div>

            {/* Framed portrait */}
            <div className="relative z-10 rounded-[2.5rem] overflow-hidden shadow-2xl ring-1 ring-gold-300/60 border-4 border-white" data-id="element-370">
              <img src="https://cdn.magicpatterns.com/uploads/5RSvfRY45AxzUB4aR22AES/stefa.png" alt="Stefania Becerra, fundadora" className="w-full h-auto object-cover" data-id="element-371" />
            </div>

            {/* Floating premium badge */}
            <div className="absolute z-20 -bottom-5 left-1/2 -translate-x-1/2 bg-white rounded-2xl shadow-xl border border-beige-200 px-6 py-3 text-center whitespace-nowrap" data-id="element-372">
              <p className="font-serif text-lg text-neutral-900 leading-tight" data-id="element-373">
                Stefania Becerra
              </p>
              <p className="text-xs text-gold-600 tracking-wide" data-id="element-374">
                Fundadora & Educadora
              </p>
            </div>
          </div>

          {/* Intro text */}
          <div className="space-y-6 text-neutral-700 text-lg leading-relaxed" data-id="element-375">
            <h3 className="font-serif text-2xl metallic-gold-text" data-id="element-376">
              Introducción
            </h3>
            <p data-id="element-377">
              Mi nombre es Stefania Becerra. Soy colombiana y hace tres años y
              medio llegué a Estados Unidos con una sola maleta — llena de
              herramientas, sueños y la firme decisión de salir adelante.
            </p>
            <p data-id="element-378">
              Dejé atrás muchas cosas, pero nunca la fe en que, con esfuerzo,
              disciplina y dedicación, podría construir un mejor futuro para mí
              y mi familia.
            </p>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-379">
          {stats.map((stat, i) => <div key={i} className="bg-white rounded-[2rem] p-8 shadow-md border border-beige-200 text-center" data-id="element-380">
              <div className="w-14 h-14 bg-beige-100 rounded-xl flex items-center justify-center mb-5 mx-auto" data-id="element-381">
                {stat.icon}
              </div>
              <p className="font-serif text-4xl metallic-gold-text mb-2" data-id="element-382">
                {stat.value}
              </p>
              <p className="text-neutral-600" data-id="element-383">{stat.label}</p>
            </div>)}
        </div>
        <p className="text-center font-serif italic text-gold-600 mb-16" data-id="element-384">
          — Stefania Becerra
        </p>

        {/* Story blocks */}
        <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-385">
          {textBlocks.map((block, i) => <div key={i} className="bg-white/60 rounded-[2rem] p-8 border border-beige-200 backdrop-blur-sm" data-id="element-386">
              <p className="flex items-center gap-2 text-sm font-medium text-gold-600 mb-2" data-id="element-387">
                <block.icon className="w-4 h-4 metallic-gold-icon flex-shrink-0" data-id="element-388" />
                {block.flag}
              </p>
              <h3 className="font-serif text-2xl metallic-gold-text mb-4" data-id="element-389">
                {block.title}
              </h3>
              <p className="text-neutral-700 leading-relaxed" data-id="element-390">{block.body}</p>
            </div>)}
        </div>

        {/* Highlighted blockquote */}
        <blockquote className="max-w-3xl mx-auto border-l-4 border-rosegold pl-8 py-4 mb-16" data-id="element-391">
          <p className="font-serif italic text-2xl md:text-3xl text-neutral-800 leading-snug" data-id="element-392">
            “No importa de dónde vengas ni con cuánto empieces — lo importante
            es la determinación para nunca rendirse.”
          </p>
          <footer className="mt-4 text-gold-600 font-medium" data-id="element-393">
            — Stefania Becerra
          </footer>
        </blockquote>

        {/* Thank you note */}
        <div className="max-w-3xl mx-auto text-center mb-20" data-id="element-394">
          <h3 className="font-serif text-2xl metallic-gold-text mb-4" data-id="element-395">
            Agradecimiento
          </h3>
          <p className="font-serif italic text-lg text-neutral-700 leading-relaxed" data-id="element-396">
            “Gracias a cada persona que ha confiado en mí, en mi trabajo y en mi
            talento. Ustedes han sido parte fundamental de este sueño que hoy
            continúa creciendo. Este es solo el comienzo de todo lo que está por
            venir.”
          </p>
        </div>

        {/* Horizontal timeline */}
        <div className="mb-16" data-id="element-397">
          <h3 className="font-serif text-2xl md:text-3xl text-center text-neutral-900 mb-12" data-id="element-398">
            Mi <span className="metallic-gold-text italic" data-id="element-399">trayectoria</span>
          </h3>
          <div className="relative" data-id="element-400">
            <div className="hidden md:block absolute top-7 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold-200 to-transparent z-0" data-id="element-401"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative z-10" data-id="element-402">
              {timeline.map((item, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-403">
                  <div className="w-14 h-14 bg-white rounded-full shadow-md border-2 border-gold-100 flex items-center justify-center mb-5" data-id="element-404">
                    <MapPin className="w-6 h-6 metallic-gold-icon" data-id="element-405" />
                  </div>
                  <p className="font-serif text-2xl metallic-gold-text mb-2" data-id="element-406">
                    {item.year}
                  </p>
                  <p className="font-medium text-neutral-900 mb-1" data-id="element-407">
                    {item.place}
                  </p>
                  <p className="text-sm text-neutral-600 leading-relaxed max-w-xs" data-id="element-408">
                    {item.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center" data-id="element-409">
          <a href="#servicios" className="inline-flex items-center justify-center metallic-gold-button text-white px-8 py-4 rounded-full font-medium text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1" data-id="element-410">
            Conoce nuestros servicios
          </a>
        </div>
      </div>
    </section>;
};